import React, { useEffect, useState } from "react";
import { Container } from "../../theme";
import { HeroContent } from "../../hooks/usePersistentContent";

export interface HeroProps {
  content: HeroContent;
}

export const Hero: React.FC<HeroProps> = ({ content }) => {
  const primaryVideoSrc: string = (content.video || "").trim() || "/assets/background_video.mp4";
  const mobileVideoSrc: string | undefined = content.videoMobile?.trim() ? content.videoMobile.trim() : undefined;
  const posterSrc: string | undefined = content.videoPoster?.trim() ? content.videoPoster.trim() : undefined;
  const ctaText: string = content.cta?.trim() ? content.cta.trim() : "צור קשר";
  const [videoReady, setVideoReady] = useState<boolean>(false);
  const [contentVisible, setContentVisible] = useState<boolean>(false);

  useEffect(() => {
    if (videoReady) {
      requestAnimationFrame(() => setContentVisible(true));
      return;
    }
    const timeoutId = window.setTimeout(() => {
      setVideoReady(true);
    }, 3000);
    return () => window.clearTimeout(timeoutId);
  }, [videoReady]);

  const handleVideoReady = (): void => {
    setVideoReady(true);
  };

  const handleVideoError = (): void => {
    setVideoReady(true);
  };

  return (
    <div id="hero" className="relative h-[85vh] min-h-[600px] w-full overflow-hidden" style={{ backgroundColor: '#3b2a1c' }}>
      {/* Background Video */}
      <video
        className="absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ease-in-out"
        autoPlay
        muted
        loop
        playsInline
        poster={posterSrc || "/assets/about_us.png"}
        preload="auto"
        aria-hidden="true"
        // @ts-expect-error fetchpriority is valid HTML but not yet in React types
        fetchpriority="high"
        onCanPlay={handleVideoReady}
        onCanPlayThrough={handleVideoReady}
        onLoadedData={handleVideoReady}
        onLoadedMetadata={handleVideoReady}
        onError={handleVideoError}
      >
        {mobileVideoSrc && <source src={mobileVideoSrc} media="(max-width: 768px)" />}
        {primaryVideoSrc && <source src={primaryVideoSrc} />}
      </video>
      
      {/* Professional Multi-layer Overlay */}
      {/* 1. Base dimming */}
      <div className="absolute inset-0 bg-black/10" />
      
      {/* 2. Top gradient for depth */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-transparent opacity-60" />
      
      {/* 3. Subtle vignette for depth */}
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.15) 100%)" }} />

      {/* Loading State */}
      <div
        className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${videoReady ? "opacity-0 pointer-events-none" : "opacity-100"}`}
        aria-hidden={videoReady}
      >
        <div className="flex h-16 w-16 items-center justify-center rounded-full border-4 border-[#e7cfb6] border-t-brand animate-spin" />
      </div>

      <Container className="relative h-full flex items-center justify-center text-center px-6">
        <div className="w-full max-w-4xl pt-16 md:pt-0">
          <div className="flex justify-center">
            {/* Main Content Card - Professional Glassmorphism */}
            <div className={`relative w-full max-w-3xl transition-all duration-1000 transform ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              {/* Decorative background glow */}
              <div
                className="pointer-events-none absolute -inset-10 rounded-[50px] bg-[radial-gradient(circle_at_center,_rgba(255,255,255,0.15),_rgba(255,255,255,0))] opacity-70 blur-2xl"
                aria-hidden="true"
              />
              
              <div className="glass-card relative flex flex-col items-center rounded-[32px] px-8 py-16 md:px-12 md:py-20 text-ink">
                
                {/* Tagline */}
                {content.tagline && (
                  <div
                    className="flex items-center gap-4 mb-8 animate-fade-in-up"
                    style={{ animationDelay: "200ms" }}
                  >
                    <span className="h-px w-10 md:w-16 bg-gradient-to-r from-transparent to-brand/60" />
                    <span
                      className="menu-font uppercase tracking-[0.35em] text-sm md:text-base font-semibold text-brand-dark"
                    >
                      {content.tagline}
                    </span>
                    <span className="h-px w-10 md:w-16 bg-gradient-to-l from-transparent to-brand/60" />
                  </div>
                )}
                
                {/* Main Title */}
                <div
                  className="relative w-full mb-6 animate-fade-in-up"
                  style={{ animationDelay: "400ms" }}
                >
                  <h1 className="brand-font text-[42px] leading-[1.1] sm:text-[52px] md:text-[68px] lg:text-[84px] font-semibold text-brand relative z-10 text-shadow-md">
                    <span className="relative inline-block">
                      {content.title}
                      {/* Refined underline accent */}
                      <svg className="absolute w-full h-3 -bottom-1 left-0 text-brand opacity-40" viewBox="0 0 100 10" preserveAspectRatio="none">
                        <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="2" fill="none" />
                      </svg>
                    </span>
                  </h1>
                </div>

                {/* Subtitle */}
                <p
                  className="menu-font text-[18px] leading-relaxed sm:text-[20px] lg:text-[24px] text-ink-dark font-medium max-w-2xl mx-auto animate-fade-in-up"
                  style={{ animationDelay: "600ms", textShadow: "0 1px 1px rgba(255,255,255,0.5)" }}
                >
                  {content.subtitle}
                </p>

                {/* CTA Button */}
                {(content.ctaEnabled === true || content.ctaEnabled === undefined) && (
                  <div
                    className="mt-10 animate-fade-in-up"
                    style={{ animationDelay: "800ms" }}
                  >
                    <a 
                      href="#contact" 
                      className="btn-brown text-base md:text-lg px-10 py-4 shadow-[0_10px_30px_rgba(116,66,29,0.3)] hover:shadow-[0_15px_40px_rgba(116,66,29,0.4)] hover:-translate-y-1 transition-all duration-300"
                    >
                      {ctaText}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Container>
      
      {/* Clean bottom edge */}
      <div className="absolute bottom-0 inset-x-0 h-px bg-surface-bg" />
    </div>
  );
};

export default Hero;
